/**
 * Archive — full browsable record of campaigns, editorials and covers
 * (IMG "From the Archive" equivalent). Filterable by type via ?type= param.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { usePageMeta, compactCredits } from "../lib/usePageMeta";

const TABS = [
  { key: "all", label: "All" },
  { key: "campaign", label: "Campaigns" },
  { key: "editorial", label: "Editorials" },
  { key: "cover", label: "Covers" },
];

export default function Archive() {
  usePageMeta("The Archive — Ege Models", "Campaigns, editorials and covers by Ege Models talent.");
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const tab = TABS.some((t) => t.key === params.get("type")) ? params.get("type") : "all";

  useEffect(() => {
    api.get("/latest")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]));
  }, []);

  const visible = useMemo(() => {
    if (!items) return [];
    return tab === "all" ? items : items.filter((w) => (w.type || "campaign") === tab);
  }, [items, tab]);

  const setTab = (key) => {
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("type"); else next.set("type", key);
    setParams(next, { replace: true });
  };

  return (
    <div data-testid="archive-page" className="pt-28 lg:pt-36 pb-24 bg-white min-h-screen">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <p className="label-overline text-zinc-500">Ege Models · Record of work</p>
        <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl tracking-tighter font-light mt-6 leading-[0.9]">
          The<br/><span className="italic text-zinc-500">archive.</span>
        </h1>

        {/* Type tabs */}
        <div className="mt-12 flex items-center gap-7 border-b border-black/10 pb-4 overflow-x-auto no-scrollbar">
          {TABS.map((t) => {
            const count = !items ? 0 : t.key === "all" ? items.length : items.filter((w) => (w.type || "campaign") === t.key).length;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`archive-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`label-overline pb-1 border-b whitespace-nowrap transition-colors ${
                  tab === t.key ? "text-black border-black" : "text-zinc-400 border-transparent hover:text-black"
                }`}
              >
                {t.label}{items ? ` (${count})` : ""}
              </button>
            );
          })}
        </div>

        {items === null ? (
          <div className="py-24 flex justify-center"><Loader2 size={20} className="animate-spin text-zinc-400"/></div>
        ) : visible.length === 0 ? (
          <p data-testid="archive-empty" className="py-24 text-center label-overline text-zinc-500">
            Nothing here yet — new work is added as it publishes.
          </p>
        ) : (
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-4 md:gap-x-6 gap-y-10 md:gap-y-14">
            {visible.map((it) => (
              <Link
                key={it.id}
                to={`/news/${it.id}`}
                data-testid={`archive-tile-${it.id}`}
                className="group block focus:outline-none focus:ring-1 focus:ring-black"
              >
                <div className="relative aspect-[3/4] bg-[#F5F5F5] overflow-hidden">
                  <img
                    src={it.image_url}
                    alt={`${it.brand}${it.subtitle ? " — " + it.subtitle : ""}`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                  <span className="absolute top-3 left-3 label-overline text-[9px] text-white bg-black/60 backdrop-blur-sm px-2 py-1">
                    {(it.type || "campaign")}
                  </span>
                </div>
                <div className="mt-3">
                  <p className="font-serif text-lg leading-tight">
                    {it.brand}
                    {it.subtitle && <span className="italic text-zinc-500"> — {it.subtitle}</span>}
                  </p>
                  {compactCredits(it) && (
                    <p className="mt-1.5 text-[10px] uppercase tracking-widest text-zinc-500 leading-relaxed line-clamp-2">
                      {compactCredits(it)}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
